import { useEffect, useState } from 'react';
import type { LocalVideoTrack } from 'livekit-client';

/**
 * What a publication is actually managing to send.
 *
 * A clip that stutters has two possible causes and they need opposite fixes:
 * the uplink cannot carry the bitrate, or this machine cannot encode it. From
 * a picture they look identical, and WebRTC has been answering the question
 * all along - `qualityLimitationReason` on the outbound stream says which,
 * in one word.
 *
 * Read from the sender rather than inferred, and shown rather than logged:
 * the person who can act on it is the one watching the clip.
 */

export interface SendStats {
  /** Kilobits per second across every simulcast layer, over the last sample. */
  kbps: number;
  /** The highest layer's frame rate, which is the one the audience sees. */
  fps: number;
  /**
   * `bandwidth`, `cpu`, `other` or `none`, from the highest layer.
   *
   * `none` with a stutter means the fault is not in the encoder at all - look
   * at the source, the decode, or the machine's own compositing.
   */
  limitedBy: string;
  /** Packets the far end reported lost, over the last sample. */
  lostPackets: number;
  /** Milliseconds, as last reported by the far end. */
  rtt: number | null;
  /** The size the highest layer is actually being encoded at. */
  size: string;
}

/** Sampled slowly: this is a diagnostic, and each read walks every layer. */
const SAMPLE_MS = 2000;

export function useSendStats(track: LocalVideoTrack | undefined): SendStats | null {
  // The sample is stored with the track it came from, and read back only for
  // that track. Clearing it in the effect would be a synchronous setState on
  // every change of source, and would still show one stale sample; this shows
  // none, and re-renders only when a sample actually arrives.
  const [sampled, setSampled] = useState<{ track: LocalVideoTrack; stats: SendStats } | null>(null);

  useEffect(() => {
    if (!track) return;

    let cancelled = false;
    // Bytes are cumulative, so a rate needs the previous sample. Kept here and
    // not in state: it is an implementation detail of the arithmetic, and
    // storing it would re-render for nothing.
    let previous: { bytes: number; lost: number; at: number } | null = null;

    const sample = async () => {
      let layers;
      try {
        layers = await track.getSenderStats();
      } catch {
        // A track being republished has no sender for a moment. Not worth
        // reporting: the next sample will have one.
        return;
      }
      if (cancelled || layers.length === 0) return;

      const bytes = layers.reduce((total, layer) => total + (layer.bytesSent ?? 0), 0);
      const lost = layers.reduce((total, layer) => total + (layer.packetsLost ?? 0), 0);
      const at = layers[0]?.timestamp ?? performance.now();

      // The layer the audience actually receives when the network allows it.
      const top = layers.reduce((best, layer) =>
        layer.frameWidth * layer.frameHeight > best.frameWidth * best.frameHeight ? layer : best,
      );

      const elapsed = previous ? (at - previous.at) / 1000 : 0;
      const next: SendStats = {
        kbps:
          previous && elapsed > 0 ? Math.round(((bytes - previous.bytes) * 8) / elapsed / 1000) : 0,
        fps: Math.round(top.framesPerSecond ?? 0),
        limitedBy: top.qualityLimitationReason ?? 'none',
        lostPackets: previous ? Math.max(0, lost - previous.lost) : 0,
        rtt: top.roundTripTime !== undefined ? Math.round(top.roundTripTime * 1000) : null,
        size: `${top.frameWidth}x${top.frameHeight}`,
      };
      previous = { bytes, lost, at };
      if (!cancelled) setSampled({ track, stats: next });
    };

    void sample();
    const timer = window.setInterval(() => void sample(), SAMPLE_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [track]);

  return sampled && sampled.track === track ? sampled.stats : null;
}
